import { configureStore, combineReducers } from '@reduxjs/toolkit'
import storage from 'redux-persist/lib/storage'
import { createMigrate, createTransform, persistReducer, persistStore } from 'redux-persist'

import widgetReducer from './widget'
import messageReducer from './messages'
import settingsReducer from './settings'
import appVersionReducer from './app-version'
import consentReducer from './consent'
import { logger } from 'redux-logger'

const migrations = {
  // Chats persisted before the multi-area migration only carry areaId/area:
  // convert them to areaIds/areas (mirrors the server-side mongo migration).
  // Crucial for guests, whose chat list lives only in the persisted store.
  1: (state: any) => ({
    ...state,
    messageState: {
      ...state?.messageState,
      chats: Object.fromEntries(
        Object.entries(state?.messageState?.chats ?? {}).map(([id, chat]: [string, any]) => [
          id,
          {
            ...chat,
            areaIds: chat.areaIds?.length ? chat.areaIds : chat.areaId ? [chat.areaId] : [],
            areas: chat.areas?.length ? chat.areas : chat.area ? [chat.area] : [],
          },
        ]),
      ),
    },
  }),
}

// Persist only the chat list/identity. Messages and conversation status are runtime: messages are
// Messages persist (optimistic in full sense): a refresh keeps the user's own messages instead of
// dropping them. Completed exchanges reconcile on reload via setChatMessages (server _id wins).
// Worst case is a question without an answer yet — never an answer without its question.
// Status and loading stay runtime-only: persisting them would strand a spinner/locked input after a
// refresh (a "streaming"/"awaitingBot" status with no live request behind it).
const dropRuntimeMessageState = createTransform(
  (inboundState: any) => {
    const persisted = { ...inboundState }
    delete persisted.statusByChat
    delete persisted.openChatLoading
    return persisted
  },
  (outboundState: any) => ({
    ...outboundState,
    messagesByChat: outboundState.messagesByChat ?? {},
    statusByChat: {},
    openChatLoading: false,
  }),
  { whitelist: ['messageState'] },
)

const persistConfig = {
  key: 'runelab-bot',
  storage,
  version: 1,
  migrate: createMigrate(migrations),
  blacklist: [],
  transforms: [dropRuntimeMessageState],
}

const reducers = combineReducers({
  widgetState: widgetReducer,
  messageState: messageReducer,
  settingsState: settingsReducer,
  appVersionState: appVersionReducer,
  consentState: consentReducer,
})
const persistedReducer = persistReducer(persistConfig, reducers)

export const store = configureStore({
  reducer: persistedReducer,
  devTools: import.meta.env.MODE !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(
      import.meta.env.MODE === 'development' ? [logger as any] : [],
    ),
})
export const persistor = persistStore(store)
